'use client';

import { useContext, useEffect } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useAppEventEmitter from './useAppEventEmitter';
import useDriftActions from './useDriftActions';
import {
	BigNum,
	BASE_PRECISION_EXP,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	ZERO,
} from '@drift-labs/sdk';
import useCurrentSettings from './useCurrentSettings';
import { MarketOrderToastEventContext } from 'src/components/MarketOrderToasts/MarketOrderToastEventEmitterProvider';
import { MarketOrderToastOrderInfo } from 'src/components/MarketOrderToasts/MarketOrderToastStateTypes';

const usePnlModalDisplayListener = () => {
	const [savedSettings] = useCurrentSettings();
	const appEventEmitter = useAppEventEmitter();
	const marketOrderToastEventContext = useContext(MarketOrderToastEventContext);
	const marketOrderToastEventsSubject = marketOrderToastEventContext.subject;
	const ordersAwaitingPnlModalDisplay = useDriftStore(
		(s) => s.ordersAwaitingPnlModalDisplay
	);
	const actions = useDriftActions();

	const displayPnlModalForFilledOrders = (
		props: Partial<MarketOrderToastOrderInfo>
	) => {
		const key = `${props.marketId.marketIndex}-${props.orderId}`;
		const orderToDisplayInModal = ordersAwaitingPnlModalDisplay[key];
		if (orderToDisplayInModal) {
			const position = orderToDisplayInModal.openPositionInfo;

			const actualExitPrice = BigNum.from(
				props.cumulativeQuoteAmountFilled,
				QUOTE_PRECISION_EXP
			)
				.shift(BASE_PRECISION_EXP)
				.div(BigNum.from(props.cumulativeBaseAmountFilled, BASE_PRECISION_EXP))
				.shiftTo(PRICE_PRECISION_EXP)
				.toNum();

			const baseAssetPriceDifference =
				position.direction === 'short'
					? (BigNum.from(position?.entryPrice, PRICE_PRECISION_EXP)?.toNum() ??
							0) - actualExitPrice
					: actualExitPrice -
					  (BigNum.from(position?.entryPrice, PRICE_PRECISION_EXP)?.toNum() ??
							0);

			const pnlDollarValue =
				BigNum.from(
					props.cumulativeBaseAmountFilled,
					BASE_PRECISION_EXP
				).toNum() * baseAssetPriceDifference;

			setDriftStore((s) => {
				delete s.ordersAwaitingPnlModalDisplay[key];
				// dont popup for lp positions since numbers can be wrong
				if (pnlDollarValue > 0 && position.lpShares.eq(ZERO)) {
					s.pnlModalPosition = {
						position,
						actualExitPrice,
						pnl: pnlDollarValue,
						isClosed: true,
						isLpPosition: false,
					};
				}
				return s;
			});

			if (
				pnlDollarValue > 0 &&
				position.lpShares.eq(ZERO) &&
				savedSettings.showPnlOnClose
			) {
				actions.showModal('showPnlModal');
			}
		}
	};

	useEffect(() => {
		const subscription = marketOrderToastEventsSubject.subscribe((event) => {
			if (event.type === 'order_completely_filled') {
				displayPnlModalForFilledOrders(event.data);
			}
		});

		return () => subscription.unsubscribe();
	}, [ordersAwaitingPnlModalDisplay, appEventEmitter]);

	const setDriftStore = useDriftStore((s) => s.set);
};

export default usePnlModalDisplayListener;
